// Badges, chips and hero insights shared by the Plan & Procurement and the
// Tender Opening list pages.

import React from 'react';
import classnames from 'classnames';
import { formatDateToddmmyyhhmm } from '../../../utils/dateformatter';
import {
  getContractType,
  getDeadlineInfo,
  getProductVisual,
} from '../utils/procurementList';
import './ListBadges.css';

export const ProductAvatar = ({ name }) => {
  const { icon, tone } = getProductVisual(name);

  return (
    <span
      className={`pv-product-avatar pv-product-avatar--${tone}`}
      title={name}
    >
      <i className={icon} aria-hidden='true' />
    </span>
  );
};

export const ContractBadge = ({ contractType }) => {
  const contract = getContractType(contractType);
  if (!contract) return null;

  return (
    <span className={`pv-contract pv-contract--${contract.tone}`}>
      {contract.label}
    </span>
  );
};

// `isClosed`: a passed deadline is expected (submitted / done), so show it muted.
export const DeadlineChip = ({ deadline, isClosed }) => {
  const info = getDeadlineInfo(deadline, isClosed);
  if (!info) return <span className='text-muted'>-</span>;

  return (
    <span
      className={`pv-deadline pv-deadline--${info.tone}`}
      title={`Proposal deadline: ${formatDateToddmmyyhhmm(deadline)}`}
    >
      <i className='ri-timer-flash-line' aria-hidden='true' />
      {info.label}
    </span>
  );
};

// Clickable counters on the gradient hero. Counters at 0 are hidden; when all
// are 0 a calm "on track" note shows instead.
export const HeroInsights = ({
  insights,
  filters,
  activeKey,
  onChange,
  noun,
  emptyLabel,
}) => {
  if (!insights) return null;

  const visible = filters.filter(filter => insights[filter.key] > 0);

  return (
    <div className='pv-insights'>
      {visible.length === 0 ? (
        <span className='pv-insight pv-insight--calm'>
          <i className='ri-checkbox-circle-line' aria-hidden='true' />
          {emptyLabel}
        </span>
      ) : (
        visible.map(filter => {
          const isActive = activeKey === filter.key;
          return (
            <button
              key={filter.key}
              type='button'
              className={classnames(
                'pv-insight',
                `pv-insight--${filter.tone}`,
                {
                  'is-active': isActive,
                }
              )}
              aria-pressed={isActive}
              title={isActive ? 'Show all' : `Show only these ${noun}s`}
              onClick={() => onChange(isActive ? '' : filter.key)}
            >
              <span className='pv-insight-dot' />
              <strong>{insights[filter.key]}</strong>
              {noun} {filter.label}
            </button>
          );
        })
      )}
    </div>
  );
};

// Toolbar chip for the hero filter that is currently applied to the table.
export const QuickFilterChip = ({ filter, noun, onClear }) => {
  if (!filter) return null;

  return (
    <span className={`pv-filter-chip pv-filter-chip--${filter.tone}`}>
      <i className={filter.icon} aria-hidden='true' />
      {noun} {filter.label}
      <button
        type='button'
        onClick={onClear}
        aria-label='Remove filter'
        title='Remove filter'
      >
        <i className='ri-close-line' aria-hidden='true' />
      </button>
    </span>
  );
};
